import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { FieldInfoModeService } from '../field-info/field-info-mode.service';
import { FieldInfoService } from '../field-info/field-info.service';
import { FieldInfoPopoverComponent } from '../field-info/field-info-popover.component';

@Component({
  selector: 'cs-form-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
})
export class FormFieldComponent implements OnDestroy {
  @Input({ required: true }) label!: string;
  @Input() fieldKey?: string;
  @Input() required = false;
  @Input() hint?: string;

  @ViewChild('infoTrigger') infoTrigger?: ElementRef<HTMLButtonElement>;

  private readonly overlay = inject(Overlay);
  private readonly fieldInfo = inject(FieldInfoService);
  private readonly mode = inject(FieldInfoModeService);

  private overlayRef?: OverlayRef;
  readonly isOpen = signal(false);

  readonly modeEnabled = this.mode.enabled;

  readonly hasInfo = computed(() => {
    return !!this.fieldKey && this.fieldInfo.has(this.fieldKey);
  });

  readonly showInfoIcon = computed(() => this.modeEnabled() && this.hasInfo());

  toggleInfo(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  private open(): void {
    if (!this.fieldKey || !this.infoTrigger) return;
    const info = this.fieldInfo.get(this.fieldKey);
    if (!info) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.infoTrigger)
      .withPositions([
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      ])
      .withFlexibleDimensions(false)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    const portal = new ComponentPortal(FieldInfoPopoverComponent);
    const ref = this.overlayRef.attach(portal);
    ref.instance.info = info;
    ref.instance.closed.subscribe(() => this.close());

    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.isOpen.set(true);
  }

  private close(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.isOpen.set(false);
  }

  ngOnDestroy(): void {
    this.close();
  }
}
